import { useState, useRef } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Star, Upload, Video, Image as ImageIcon, Smile, Loader2, CheckCircle, Clock, XCircle, Trash2, ExternalLink } from "lucide-react";
import type { Testimonial } from "@shared/schema";

export default function TestimonialsPage() {
  const { toast } = useToast();
  const { user } = useAuth();
  const [rating, setRating] = useState(5);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [testimonialText, setTestimonialText] = useState("");
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [videoUrl, setVideoUrl] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsDialogOpen, setTermsDialogOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: testimonials = [], isLoading } = useQuery<Testimonial[]>({
    queryKey: ['/api/testimonials'],
  });

  const createMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await apiRequest('POST', '/api/testimonials', data);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/testimonials'] });
      toast({
        title: "Thank you!",
        description: "Your testimonial has been submitted for review.",
      });
      setRating(5);
      setTestimonialText("");
      setPhotoUrl(null);
      setVideoUrl("");
      setTermsAccepted(false);
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to submit testimonial",
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest('DELETE', `/api/testimonials/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/testimonials'] });
      toast({
        title: "Deleted",
        description: "Your testimonial has been deleted.",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to delete testimonial",
        variant: "destructive",
      });
    },
  });

  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: "File too large",
          description: "Please upload a photo under 5MB",
          variant: "destructive",
        });
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoUrl(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = () => {
    if (!testimonialText.trim()) {
      toast({
        title: "Required",
        description: "Please write your testimonial",
        variant: "destructive",
      });
      return;
    }
    if (!termsAccepted) {
      toast({
        title: "Required",
        description: "Please accept the terms and conditions",
        variant: "destructive",
      });
      return;
    }

    const displayName = user?.firstName && user?.lastName 
      ? `${user.firstName} ${user.lastName}`.trim()
      : user?.email || "Anonymous User";
      
    createMutation.mutate({
      rating,
      testimonialText: testimonialText.trim(),
      photoUrl,
      videoUrl: videoUrl.trim() || null,
      termsAccepted,
      displayName,
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
        return <Badge className="bg-green-500"><CheckCircle className="w-3 h-3 mr-1" /> Approved</Badge>;
      case 'rejected':
        return <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" /> Rejected</Badge>;
      default:
        return <Badge variant="secondary"><Clock className="w-3 h-3 mr-1" /> Pending Review</Badge>;
    }
  };

  return (
    <div className="w-full max-w-full py-6 md:py-8 px-4 md:px-6">
      <div className="flex items-center gap-3 mb-8">
        <Smile className="w-8 h-8 text-primary" />
        <div>
          <h1 className="text-3xl font-bold" data-testid="text-page-title">Share Your Experience</h1>
          <p className="text-muted-foreground">We'd love to hear about your journey with GoalVia</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Submission Form */}
        <Card>
          <CardHeader>
            <CardTitle>Submit Your Testimonial</CardTitle>
            <CardDescription>
              Share your success story and help inspire others
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Star Rating */}
            <div className="space-y-2">
              <Label>Your Rating</Label>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setRating(star)}
                    onMouseEnter={() => setHoveredRating(star)}
                    onMouseLeave={() => setHoveredRating(0)}
                    className="p-1 transition-transform hover:scale-110"
                    data-testid={`button-star-${star}`}
                  >
                    <Star
                      className={`w-8 h-8 ${
                        star <= (hoveredRating || rating)
                          ? "fill-yellow-400 text-yellow-400"
                          : "text-gray-300"
                      }`}
                    />
                  </button>
                ))}
              </div>
            </div>

            {/* Testimonial Text */}
            <div className="space-y-2">
              <Label htmlFor="testimonial">Your Testimonial</Label>
              <Textarea
                id="testimonial"
                placeholder="Tell us about your experience with GoalVia. How has it helped you achieve your goals?"
                value={testimonialText}
                onChange={(e) => setTestimonialText(e.target.value)}
                rows={5}
                data-testid="input-testimonial-text"
              />
            </div>

            {/* Profile Photo */}
            <div className="space-y-2">
              <Label>Profile Photo (Optional)</Label>
              <div className="flex items-center gap-4">
                <Avatar className="w-16 h-16">
                  <AvatarImage src={photoUrl || undefined} />
                  <AvatarFallback><ImageIcon className="w-6 h-6" /></AvatarFallback>
                </Avatar>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoUpload}
                  className="hidden"
                  ref={fileInputRef}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="button-upload-photo"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Upload Photo
                </Button>
                {photoUrl && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setPhotoUrl(null)}
                  >
                    Remove
                  </Button>
                )}
              </div>
            </div>

            {/* Video URL */}
            <div className="space-y-2">
              <Label htmlFor="video">Video Testimonial URL (Optional)</Label>
              <div className="flex gap-2">
                <Video className="w-5 h-5 text-muted-foreground mt-2" />
                <Input
                  id="video"
                  placeholder="https://youtube.com/watch?v=... or Loom link"
                  value={videoUrl}
                  onChange={(e) => setVideoUrl(e.target.value)}
                  data-testid="input-video-url"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Share a YouTube, Loom, or other video link
              </p>
            </div>

            <Separator />

            {/* Terms Checkbox */}
            <div className="flex items-start space-x-3">
              <Checkbox
                id="terms"
                checked={termsAccepted}
                onCheckedChange={(checked) => setTermsAccepted(checked as boolean)}
                data-testid="checkbox-terms"
              />
              <div className="space-y-1 leading-none">
                <Label htmlFor="terms" className="cursor-pointer">
                  I accept the{" "}
                  <button
                    type="button"
                    className="text-primary underline hover:no-underline"
                    onClick={() => setTermsDialogOpen(true)}
                    data-testid="button-view-terms"
                  >
                    Terms & Conditions
                  </button>
                </Label>
                <p className="text-xs text-muted-foreground">
                  Required to submit your testimonial
                </p>
              </div>
            </div>

            {/* Submit Button */}
            <Button
              onClick={handleSubmit}
              disabled={!termsAccepted || !testimonialText.trim() || createMutation.isPending}
              className="w-full"
              data-testid="button-submit-testimonial"
            >
              {createMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                "Submit Testimonial"
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Live Preview */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Smile className="w-5 h-5" />
                Live Preview
              </CardTitle>
              <CardDescription>
                This is how your testimonial will appear when approved
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="bg-muted/50 rounded-lg p-6 space-y-4">
                <div className="flex items-center gap-4">
                  <Avatar className="w-12 h-12">
                    <AvatarImage src={photoUrl || undefined} />
                    <AvatarFallback>
                      <Smile className="w-6 h-6" />
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <div className="flex gap-0.5">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star
                          key={star}
                          className={`w-4 h-4 ${
                            star <= rating
                              ? "fill-yellow-400 text-yellow-400"
                              : "text-gray-300"
                          }`}
                        />
                      ))}
                    </div>
                    <p className="text-sm text-muted-foreground">GoalVia User</p>
                  </div>
                </div>
                <blockquote className="text-foreground italic">
                  "{testimonialText || "Your testimonial will appear here..."}"
                </blockquote>
                {videoUrl && /^https?:\/\//i.test(videoUrl) && (
                  <a
                    href={videoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm text-primary hover:underline"
                  >
                    <Video className="w-4 h-4" />
                    Watch Video Testimonial
                    <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
            </CardContent>
          </Card>

          {/* My Testimonials */}
          {testimonials.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Your Submitted Testimonials</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {isLoading ? (
                  <div className="flex justify-center py-4">
                    <Loader2 className="w-6 h-6 animate-spin" />
                  </div>
                ) : (
                  testimonials.map((testimonial) => (
                    <div key={testimonial.id} className="border rounded-lg p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex gap-0.5">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <Star
                              key={star}
                              className={`w-4 h-4 ${
                                star <= testimonial.rating
                                  ? "fill-yellow-400 text-yellow-400"
                                  : "text-gray-300"
                              }`}
                            />
                          ))}
                        </div>
                        {getStatusBadge(testimonial.status)}
                      </div>
                      <p className="text-sm">{testimonial.testimonialText}</p>
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <span>
                          Submitted {new Date(testimonial.createdAt).toLocaleDateString()}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteMutation.mutate(testimonial.id)}
                          disabled={deleteMutation.isPending}
                          data-testid={`button-delete-testimonial-${testimonial.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      {testimonial.adminNote && (
                        <div className="bg-muted p-2 rounded text-sm">
                          <strong>Admin Note:</strong> {testimonial.adminNote}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Terms Dialog */}
      <Dialog open={termsDialogOpen} onOpenChange={setTermsDialogOpen}>
        <DialogContent className="w-screen h-screen max-w-none max-h-none m-0 rounded-none overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Testimonial Terms & Conditions</DialogTitle>
            <DialogDescription>
              Please read and understand these terms before submitting
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4 text-sm">
            <p>By submitting a testimonial, you agree to the following terms:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li>
                <strong>Property of Fintomic:</strong> Your testimonial becomes the property of Fintomic LLC and may be used for marketing and promotional purposes.
              </li>
              <li>
                <strong>Unpaid Endorsement:</strong> Your testimonial is provided voluntarily and without compensation. This is an unpaid endorsement of GoalVia.
              </li>
              <li>
                <strong>Usage Rights:</strong> Fintomic LLC has the right to use your testimonial, including any photos or videos submitted, across any platform or medium, including but not limited to websites, social media, advertisements, and marketing materials.
              </li>
              <li>
                <strong>Authenticity:</strong> You confirm that your testimonial represents your genuine experience and is accurate to the best of your knowledge.
              </li>
              <li>
                <strong>Moderation:</strong> Fintomic LLC reserves the right to edit, refuse, or remove any testimonial at its sole discretion.
              </li>
            </ul>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTermsDialogOpen(false)}>
              Close
            </Button>
            <Button onClick={() => {
              setTermsAccepted(true);
              setTermsDialogOpen(false);
            }}>
              I Accept
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
